// INSIDO (16-mensagens-de-voz): pure waveform maths for voice messages —
// peaks from PCM samples / an AudioBuffer, resampling, byte (0..255) packing
// and live analyser levels. No DOM / Solid imports (node contract-tested).

/** Number of bars stored in the filename and drawn in the bubble */
export const VOICE_BARS = 48;

/** Minimal structural view of a Web Audio `AudioBuffer` */
export interface PcmSource {
  numberOfChannels: number;
  length: number;
  getChannelData(channel: number): ArrayLike<number>;
}

/**
 * Normalise a list of non-negative levels so the loudest becomes 1.
 * An all-silent list stays all zeros.
 * @param levels Raw levels
 * @returns Levels in 0..1
 */
export function normalise(levels: number[]): number[] {
  let max = 0;
  for (const v of levels) if (Number.isFinite(v) && v > max) max = v;
  if (max <= 0) return levels.map(() => 0);
  return levels.map((v) =>
    Number.isFinite(v) && v > 0 ? Math.min(1, v / max) : 0,
  );
}

/**
 * Split mono PCM samples into `bars` equal buckets and take each bucket's
 * peak absolute amplitude, normalised to 0..1.
 * @param samples PCM samples (-1..1)
 * @param bars Number of output bars
 * @returns `bars` levels in 0..1
 */
export function computePeaks(
  samples: ArrayLike<number>,
  bars: number = VOICE_BARS,
): number[] {
  return peaksFromChannels([samples], bars);
}

/**
 * Same as {@link computePeaks} over several channels (max across channels).
 * @param channels One sample array per channel (equal length)
 * @param bars Number of output bars
 * @returns `bars` levels in 0..1
 */
export function peaksFromChannels(
  channels: ArrayLike<number>[],
  bars: number = VOICE_BARS,
): number[] {
  const count = Math.max(0, Math.floor(bars));
  const length = channels.reduce((n, c) => Math.max(n, c.length), 0);
  const raw = new Array<number>(count).fill(0);
  if (!count || !length) return raw;

  for (let bar = 0; bar < count; bar++) {
    const start = Math.floor((bar * length) / count);
    const end = Math.max(start + 1, Math.floor(((bar + 1) * length) / count));
    let peak = 0;
    for (const channel of channels) {
      for (let i = start; i < end && i < channel.length; i++) {
        const v = Math.abs(channel[i]);
        if (v > peak) peak = v;
      }
    }
    raw[bar] = peak;
  }

  return normalise(raw);
}

/**
 * Peaks of a decoded `AudioBuffer`.
 * @param buffer Decoded audio
 * @param bars Number of output bars
 * @returns `bars` levels in 0..1
 */
export function peaksFromAudioBuffer(
  buffer: PcmSource,
  bars: number = VOICE_BARS,
): number[] {
  const channels: ArrayLike<number>[] = [];
  for (let c = 0; c < buffer.numberOfChannels; c++) {
    channels.push(buffer.getChannelData(c));
  }
  return peaksFromChannels(channels, bars);
}

/**
 * Resample a list of levels to a different bar count (max per bucket when
 * shrinking, nearest neighbour when growing).
 * @param levels Levels in 0..1
 * @param bars Target bar count
 * @returns `bars` levels
 */
export function resampleLevels(levels: number[], bars: number): number[] {
  const count = Math.max(0, Math.floor(bars));
  if (!levels.length) return new Array<number>(count).fill(0);
  const out: number[] = [];
  for (let bar = 0; bar < count; bar++) {
    const start = Math.floor((bar * levels.length) / count);
    const end = Math.max(
      start + 1,
      Math.floor(((bar + 1) * levels.length) / count),
    );
    let peak = 0;
    for (let i = start; i < end && i < levels.length; i++) {
      peak = Math.max(peak, levels[i]);
    }
    out.push(peak);
  }
  return out;
}

/**
 * Pack 0..1 levels into bytes 0..255 (for the filename).
 * @param levels Levels in 0..1
 * @returns Integers 0..255
 */
export function levelsToBytes(levels: number[]): number[] {
  return levels.map((v) =>
    Math.round(Math.min(1, Math.max(0, Number.isFinite(v) ? v : 0)) * 255),
  );
}

/**
 * Unpack bytes 0..255 into 0..1 levels.
 * @param bytes Integers 0..255
 * @returns Levels in 0..1
 */
export function bytesToLevels(bytes: number[]): number[] {
  return bytes.map((b) => Math.min(255, Math.max(0, b)) / 255);
}

/**
 * Live levels for the recording strip from an AnalyserNode time-domain
 * snapshot (`getByteTimeDomainData`, silence = 128): one value 0..1 per
 * bucket, not normalised (so silence looks silent).
 * @param timeDomain Byte time-domain data
 * @param bars Number of output bars
 * @returns `bars` levels in 0..1
 */
export function liveLevels(
  timeDomain: ArrayLike<number>,
  bars: number,
): number[] {
  const count = Math.max(0, Math.floor(bars));
  const out = new Array<number>(count).fill(0);
  if (!timeDomain.length || !count) return out;
  for (let bar = 0; bar < count; bar++) {
    const start = Math.floor((bar * timeDomain.length) / count);
    const end = Math.max(
      start + 1,
      Math.floor(((bar + 1) * timeDomain.length) / count),
    );
    let peak = 0;
    for (let i = start; i < end && i < timeDomain.length; i++) {
      peak = Math.max(peak, Math.abs(timeDomain[i] - 128) / 128);
    }
    out[bar] = Math.min(1, peak);
  }
  return out;
}

/**
 * Root-mean-square level of an AnalyserNode time-domain snapshot (0..1).
 * @param timeDomain Byte time-domain data (silence = 128)
 * @returns Level 0..1
 */
export function rmsLevel(timeDomain: ArrayLike<number>): number {
  if (!timeDomain.length) return 0;
  let sum = 0;
  for (let i = 0; i < timeDomain.length; i++) {
    const v = (timeDomain[i] - 128) / 128;
    sum += v * v;
  }
  return Math.min(1, Math.sqrt(sum / timeDomain.length));
}
